import dayjs from 'dayjs';
import { MigrateDelegate } from '@/renderer/scripts/CoSync/SyncCenter/Migrate/MigrateDelegate';
import { getCoSyncDB, IMigration } from '@/renderer/scripts/CoSync/SyncCenter/Repo/CoSyncDB';
import CoSyncMigrationDAO from '@/renderer/scripts/CoSync/SyncCenter/Repo/CoSyncMigrationDAO';
import SyncGlobalRepo from '@/renderer/scripts/CoSync/SyncCenter/Repo/SyncGlobalRepo';
import SyncDB from '@/renderer/scripts/CoSync/SyncCenter/Migrate/Old/SyncDB';
import {
  ConflictStrategy,
  CoSyncPath,
  CoSyncSetting,
  CoSyncTaskConfig,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';
import smh from '@/renderer/scripts/smh';
import { migrateSyncInfoParams } from '@/renderer/scripts/libs/smh-sdk-class/typings';

export class MigrationMgr {
  private centerDelegate: WeakRef<MigrateDelegate>;
  private migrationDAO: CoSyncMigrationDAO;
  private oldDB: SyncDB;
  private userInfo: CoFile.UserInfo;

  constructor(centerDelegate: WeakRef<MigrateDelegate>, syncGlobalRepo: SyncGlobalRepo, userInfo: CoFile.UserInfo) {
    this.centerDelegate = centerDelegate;
    this.migrationDAO = new CoSyncMigrationDAO(getCoSyncDB());
    this.oldDB = new SyncDB({ needClearTable: false });
    this.oldDB.setUserInfo({
      userId: userInfo.userId || 0,
      organizationId: userInfo.organizationId || 0,
      libraryId: userInfo.libraryId || '',
    });
    this.userInfo = userInfo;
  }

  public async migrateIfNeed(): Promise<void> {
    try {
      const migrationRecords = await this.migrationDAO.getAll();
      const found = migrationRecords.find(record => record.purpose === this.getPurposeFlag());
      // 已经迁移过了
      if (found) {
        logger.ui.debug('migrateIfNeed no need to migrate db: already checked before');
        return;
      }

      const oldConfigs = await this.oldDB.getSyncTasks();
      const needToMigrate = oldConfigs.length > 0;

      // 无需迁移
      if (!needToMigrate) {
        logger.ui.error('migrateIfNeed no needToMigrate');
        this.migrationDAO.add({
          purpose: this.getPurposeFlag(),
          timestampInMs: dayjs().valueOf(),
          needMigration: needToMigrate,
        } as IMigration);
        return;
      }

      // 需要迁移
      const configsToMigrated = await MigrationMgr.convertOldConfigToNewConfig(oldConfigs);
      // 无需并发跑，串行跑不占用资源
      for (const config of configsToMigrated) {
        const aCenterDelegate = this.centerDelegate.deref();
        if (!aCenterDelegate) {
          // 如果中途中断了，那就不要记录已迁移
          logger.ui.error('migrateIfNeed aCenterDelegate is undefined');
          return;
        }

        // 考虑到同步盘是小需求，迁移是小场景，迁移失败是小场景，所以这里只打日志
        // 如果展示Tips，可能会在其他页面影响用户体验
        try {
          await aCenterDelegate.addService(config);
        } catch (error: MsgError) {
          logger.ui.error('migrateIfNeed task error: ', error);
        }
      }

      // 记录已迁移
      this.migrationDAO.add({
        purpose: this.getPurposeFlag(),
        timestampInMs: dayjs().valueOf(),
        needMigration: needToMigrate,
      } as IMigration);

      logger.ui.error('migrateIfNeed have migrate', oldConfigs, configsToMigrated);
    } catch (error: MsgError) {
      logger.ui.error('migrateIfNeed error: ', error);
    }
  }

  private getPurposeFlag() {
    return `co_sync_config_migration_${this.userInfo.userId}_${this.userInfo.organizationId}`;
  }

  static getOldConfig(oldConfigs: Sync.SyncInfo[], syncId: number) {
    for (const item of oldConfigs) {
      if (item.syncId === syncId) {
        return item;
      }
    }
    return undefined;
  }

  static getSyncStatus(oldConfig: Sync.SyncInfo) {
    switch (oldConfig.status) {
      case 'stop':
        return 'stopped';
      default:
        return 'started';
    }
  }

  private static async convertOldConfigToNewConfig(oldConfigs: Sync.SyncInfo[]) {
    const list: number[] = [];
    oldConfigs.forEach(item => {
      list.push(item.syncId);
    });
    const param: migrateSyncInfoParams = {
      list,
    };
    const result = [];
    const { data } = await smh.userAPI.migrateSyncInfo(param);
    for (const item of data) {
      const oldConfig = MigrationMgr.getOldConfig(oldConfigs, item.lsyncId);
      if (!oldConfig) {
        logger.ui.info('convertOldConfigToNewConfig no find', item, oldConfigs);
        continue;
      }
      if (item.status === 'not_found') {
        // 特殊处理，其他错误,等新同步盘创建成功再运行起来再提示
        logger.ui.error('convertOldConfigToNewConfig status error', item, oldConfigs);
        continue;
      }
      logger.ui.info('convertOldConfigToNewConfig status', item, oldConfigs);
      const info: CoSyncTaskConfig = {
        syncPath: {
          localPath: item.localPath,
          remotePath: item.cloudPath.join('/'),
          spaceId: item.spaceId,
          spaceTag: item.spaceTag,
          spaceOrgId: item.orgId,
          teamId: item.teamId || 0,
          teamPath: item.teamPath ? item.teamPath.join('/') : '',
          isCreateBaseLocal: true,
        } as CoSyncPath,
        syncSetting: {
          strategy: oldConfig.strategy,
          timingType: oldConfig.syncType,
          conflictStrategy: ConflictStrategy.ASK,
          keepWhichLastStatus: MigrationMgr.getSyncStatus(oldConfig),
        } as CoSyncSetting,
      };
      result.push(info);
    }
    return result;
  }
}
